import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { TranslateService } from '@ngx-translate/core';
import { sortBy } from 'lodash';
import { combineLatest, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { IDropdownOption } from '../../../shared/components/mat-dropdown/mat-dropdown.component';
import { ELoadStatus } from '../../../state/state.interface';
import { UserEffects } from '../../../state/user/user.effects';
import { ICurrentUser, IUser } from '../../../state/user/user.interface';
import {
  HttpUtilitiesService,
  IGenericCrudRequestConstructionParameters,
  IGetManyResponse,
} from '../../../utilities/http-utilities.service';
import { IBoard } from '../../settings/boards/boards.interface';
import { EUserAssignee } from '../items.interface';

export interface IItemsFilterCardState {
  assignees: IDropdownOption[];
  assigneesLoading: ELoadStatus;
  isAssigneeSearchActive: boolean;
  boards: IBoard[];
  boardsLoading: ELoadStatus;
  itemCategories: IDropdownOption[];
  itemCategoriesLoading: ELoadStatus;
  reporters: IDropdownOption[];
  reportersLoading: ELoadStatus;
  isReporterSearchActive: boolean;
  workflowSteps: IDropdownOption[];
  workflowStepsLoading: ELoadStatus;
}

@Injectable()
export class ItemsFilterCardStore extends ComponentStore<IItemsFilterCardState> {
  public user: ICurrentUser = UserEffects.getUser();

  public readonly itemCategories = this.selectSignal((state: IItemsFilterCardState) => {
    if (state.itemCategoriesLoading !== ELoadStatus.success) {
      return [];
    }

    const categories = state.itemCategories.map((item) => ({
      id: item.id,
      name: item.isDefault ? this.translate.instant(`system.label.${item.name}`) : item.name,
    }));

    const noCategoryOption: IDropdownOption = {
      id: 0,
      name: this.translate.instant('field.noCategory'),
    };

    return [noCategoryOption, ...categories];
  });
  public readonly workflowSteps = this.selectSignal((state: IItemsFilterCardState) =>
    state.workflowSteps.map((item) => ({
      id: item.id,
      name: item.isDefault ? this.translate.instant(`system.label.${item.name}`) : item.name,
      // Carried through so a status category (KPI card) can be mapped back to its statuses.
      statusCategory: item.statusCategory,
    })),
  );
  public readonly boards = this.selectSignal((state: IItemsFilterCardState) =>
    sortBy(
      state.boards.map((item) => ({
        id: item.id,
        name: `${item.name} - ${item.key}`,
      })),
      'name',
    ),
  );
  public readonly assignees$: Observable<IDropdownOption[]> = combineLatest({
    assignees: this.select((state: IItemsFilterCardState) => state.assignees),
    isAssigneeSearchActive: this.select((state: IItemsFilterCardState) => state.isAssigneeSearchActive),
    status: this.select((state: IItemsFilterCardState) => state.assigneesLoading),
  }).pipe(
    map(({ assignees, isAssigneeSearchActive, status }) => {
      const isLoading: boolean = status === ELoadStatus.initial || status === ELoadStatus.initialLoad;

      if (isLoading) return [];

      const translatedCurrentUser = {
        id: this.user.id,
        name: `${this.translate.instant('filterCard.currentUser')} (${this.user.name})`,
      };

      const translatedUnassigned = {
        id: EUserAssignee.unassignedUser,
        name: this.translate.instant('page.items.label.unassigned'),
      };

      if (isAssigneeSearchActive) {
        return assignees.map((assignee) => (assignee.id === this.user.id ? translatedCurrentUser : assignee));
      }

      const filteredAssignees = assignees.filter((assignee) => assignee.id !== this.user.id);

      return [translatedCurrentUser, translatedUnassigned, ...filteredAssignees];
    }),
  );

  // No "unassigned" option, unlike `assignees$` — a reporter is required on every item (the create
  // form validates it), so there's nothing that "no reporter" could ever filter for.
  public readonly reporters$: Observable<IDropdownOption[]> = combineLatest({
    isReporterSearchActive: this.select((state: IItemsFilterCardState) => state.isReporterSearchActive),
    reporters: this.select((state: IItemsFilterCardState) => state.reporters),
    status: this.select((state: IItemsFilterCardState) => state.reportersLoading),
  }).pipe(
    map(({ isReporterSearchActive, reporters, status }) => {
      const isLoading: boolean = status === ELoadStatus.initial || status === ELoadStatus.initialLoad;

      if (isLoading) return [];

      const translatedCurrentUser = {
        id: this.user.id,
        name: `${this.translate.instant('filterCard.currentUser')} (${this.user.name})`,
      };

      if (isReporterSearchActive) {
        return reporters.map((reporter) => (reporter.id === this.user.id ? translatedCurrentUser : reporter));
      }

      const filteredReporters = reporters.filter((reporter) => reporter.id !== this.user.id);

      return [translatedCurrentUser, ...filteredReporters];
    }),
  );

  readonly getItemCategoryData = this.effect((trigger$) =>
    trigger$.pipe(
      switchMap(() => {
        const httpParams: HttpParams = new HttpParams().set('limit', 1000);
        this.patchState({ itemCategoriesLoading: ELoadStatus.loading });

        return this.getItemCategories(httpParams).pipe(
          tapResponse(
            (response: IGetManyResponse<IDropdownOption>) =>
              this.patchState({
                itemCategories: response.data,
                itemCategoriesLoading: ELoadStatus.success,
              }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly getWorkflowStepData = this.effect((trigger$) =>
    trigger$.pipe(
      switchMap(() => {
        const httpParams: HttpParams = new HttpParams().set('limit', 1000).set('sort', 'id,ASC');
        this.patchState({ workflowStepsLoading: ELoadStatus.loading });

        return this.getWorkflowSteps(httpParams).pipe(
          tapResponse(
            (response: IGetManyResponse<IDropdownOption>) =>
              this.patchState({
                workflowSteps: response.data,
                workflowStepsLoading: ELoadStatus.success,
              }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly getBoardData = this.effect((trigger$: Observable<IGenericCrudRequestConstructionParameters>) =>
    trigger$.pipe(
      switchMap((params: IGenericCrudRequestConstructionParameters) => {
        this.patchState({ boardsLoading: ELoadStatus.loading });
        const parameters = this.httpUtilities.insertGenericCrudRequestParameters(params);

        return this.getBoards(parameters).pipe(
          tapResponse(
            (response) =>
              this.patchState({
                boards: response.data,
                boardsLoading: ELoadStatus.success,
              }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly getAssigneeData = this.effect((trigger$: Observable<IGenericCrudRequestConstructionParameters>) =>
    trigger$.pipe(
      switchMap((params: IGenericCrudRequestConstructionParameters) => {
        this.patchState(({ assigneesLoading }) =>
          assigneesLoading === ELoadStatus.initial
            ? { assigneesLoading: ELoadStatus.initialLoad }
            : { assigneesLoading: ELoadStatus.loading },
        );
        const parameters = this.httpUtilities.insertGenericCrudRequestParameters(params);
        const isSearchActive = !!params.search?.searchText;

        return this.getUsers(parameters).pipe(
          tapResponse(
            (response) =>
              this.patchState({
                assignees: response.data,
                assigneesLoading: ELoadStatus.success,
                isAssigneeSearchActive: isSearchActive,
              }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly getReporterData = this.effect((trigger$: Observable<IGenericCrudRequestConstructionParameters>) =>
    trigger$.pipe(
      switchMap((params: IGenericCrudRequestConstructionParameters) => {
        this.patchState(({ reportersLoading }) =>
          reportersLoading === ELoadStatus.initial
            ? { reportersLoading: ELoadStatus.initialLoad }
            : { reportersLoading: ELoadStatus.loading },
        );
        const parameters = this.httpUtilities.insertGenericCrudRequestParameters(params);
        const isSearchActive = !!params.search?.searchText;

        return this.getUsers(parameters).pipe(
          tapResponse(
            (response) =>
              this.patchState({
                isReporterSearchActive: isSearchActive,
                reporters: response.data,
                reportersLoading: ELoadStatus.success,
              }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  constructor(
    private readonly http: HttpClient,
    private readonly translate: TranslateService,
    private readonly httpUtilities: HttpUtilitiesService,
  ) {
    super({
      assignees: [],
      assigneesLoading: ELoadStatus.initial,
      boards: [],
      boardsLoading: ELoadStatus.initial,
      isAssigneeSearchActive: false,
      isReporterSearchActive: false,
      itemCategories: [],
      itemCategoriesLoading: ELoadStatus.initial,
      reporters: [],
      reportersLoading: ELoadStatus.initial,
      workflowSteps: [],
      workflowStepsLoading: ELoadStatus.initial,
    });
  }

  private getItemCategories(params: HttpParams): Observable<IGetManyResponse<IDropdownOption>> {
    return this.http.get<IGetManyResponse<IDropdownOption>>('item-categories', { params });
  }

  private getWorkflowSteps(params: HttpParams): Observable<IGetManyResponse<IDropdownOption>> {
    return this.http.get<IGetManyResponse<IDropdownOption>>('workflow-steps', { params });
  }

  private getBoards(params: HttpParams): Observable<IGetManyResponse<IBoard>> {
    return this.http.get<IGetManyResponse<IBoard>>('boards', { params });
  }

  private getUsers(params: HttpParams): Observable<IGetManyResponse<IUser>> {
    return this.http.get<IGetManyResponse<IUser>>('users', { params });
  }
}
